import { IconButton, InputAdornment, TextField } from "@mui/material";
import { TextFieldStyles } from "../../styles/commonStyles";
import { useCallback, useEffect, useState } from "react";
import { validatePassword } from "./utils";
import CustomAlert from "../alert/alert.component";
import { useNavigate, useSearchParams } from "react-router";
import FormActionButton from "../buttons/formActionButton.component";
import { useDispatch, useSelector } from "react-redux";
import {
  changePasswordStart,
  clearChangePasswordState,
} from "../../store/features/changePassword/changePasswordSlice";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const ChangePasswordForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid");

  const { isLoading, success, error } = useSelector(
    (state) => state.changePassword
  );

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  });
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (!uid) {
      navigate("/login", { replace: true });
    }
  }, [uid, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearChangePasswordState());
    };
  }, [dispatch]);

  const handleInputChange = useCallback(
    (field) => (event) => {
      setFormData((prev) => ({ ...prev, [field]: event.target.value }));
      setValidationError("");
    },
    []
  );

  const togglePasswordVisibility = useCallback(
    (field) => () => {
      setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
    },
    []
  );

  const handleSubmit = useCallback(() => {
    const validationResult = validatePassword(formData.newPassword);
    if (validationResult !== true) {
      setValidationError(validationResult);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setValidationError("Le password non coincidono");
      return;
    }

    dispatch(
      changePasswordStart({
        password: formData.newPassword,
        confirm_password: formData.confirmPassword,
        uid,
      })
    );
  }, [formData, uid, dispatch]);

  useEffect(() => {
    if (success) {
      const timeoutId = setTimeout(() => {
        navigate("/login");
      }, 5000);
      return () => clearTimeout(timeoutId);
    }
  }, [navigate, success]);

  const renderPasswordAdornment = (field) => {
    return (
      <InputAdornment position="end">
        <IconButton
          aria-label={
            showPassword[field] ? "Nascondi password" : "Mostra password"
          }
          onClick={togglePasswordVisibility(field)}
          edge="end"
        >
          {showPassword[field] ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    );
  };

  return (
    <>
      <TextField
        type={showPassword.new ? "text" : "password"}
        variant="filled"
        fullWidth
        sx={TextFieldStyles}
        label="Nuova password"
        placeholder="Nuova password"
        aria-label="Nuova password"
        value={formData.newPassword}
        onChange={handleInputChange("newPassword")}
        error={
          !!validationError && validationError !== "Le password non coincidono"
        }
        helperText={
          validationError && validationError !== "Le password non coincidono"
            ? validationError
            : ""
        }
        slotProps={{
          input: {
            endAdornment: renderPasswordAdornment("new"),
          },
        }}
      />
      <TextField
        type={showPassword.confirm ? "text" : "password"}
        variant="filled"
        fullWidth
        sx={TextFieldStyles}
        label="Conferma password"
        placeholder="Conferma password"
        aria-label="Conferma password"
        value={formData.confirmPassword}
        onChange={handleInputChange("confirmPassword")}
        error={validationError === "Le password non coincidono"}
        helperText={
          validationError === "Le password non coincidono"
            ? validationError
            : ""
        }
        slotProps={{
          input: {
            endAdornment: renderPasswordAdornment("confirm"),
          },
        }}
      />
      {success && (
        <CustomAlert
          severity="success"
          message="Password aggiornata con successo. A breve verrai indirizzato alla pagina di Login"
        />
      )}
      {error && <CustomAlert severity="error" message={error} />}
      <FormActionButton
        label="Conferma nuova password"
        onClick={handleSubmit}
        variant="contained"
        disabled={isLoading || success}
        style={{
          textTransform: "none",
          height: "50px",
          borderRadius: 0,
          width: "100%",
        }}
      />
    </>
  );
};

export default ChangePasswordForm;
